import React, { useState, useEffect, useMemo } from 'react';
import { Card, Row, Col, ListGroup, Badge, Alert, Table, Form, Button, Modal, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { DEFAULT_CURRENCY } from '../shared/constants';
import { getAllCurrencies, getSelectedCurrency, setSelectedCurrency, convertCurrency, getHistoricalRates } from '../shared/currencyService';
import { normalizeCurrencyValue, generateUUID, formatDateWithLocale } from '../shared/utils';
import { clog, cwarn, cerror } from '../shared/consoleLogger';

// Import shared activity utilities
import { getActivityStyle as getSharedActivityStyle } from '../shared/activityUtils';

// Use shared function, but keep local name for compatibility
const getActivityStyle = (type, activity = {}) => {
  return getSharedActivityStyle(type, activity);
};

// Extract all costs from plan data
// Note: This function is called outside the component, so it can't use useTranslation
// Day labels will be translated in the component where they're displayed
const extractCosts = (parsedData) => {
  if (!parsedData || !parsedData.days) return { items: [], totals: [] };
  
  clog('Extracting costs from plan data...');
  
  const costItems = [];
  let totalByCurrency = {};
  
  // Process each activity with a cost
  parsedData.days.forEach((day, dayIndex) => {
    day.activities?.forEach((activity, actIndex) => {
      if (activity.cost) {
        // Cost is always an object - simplify extraction
        const costValue = normalizeCurrencyValue(activity.cost.value);
        const currency = activity.cost.currency;
        
        // Only include if we have a cost value
        if (costValue && !isNaN(costValue) && costValue > 0) {
          // Generate a unique ID for this cost item
          const costId = `day${dayIndex}_act${actIndex}`;
          
          // Check if the cost has payment information
          const isPaid = !!activity.cost.rates;
          const paidDate = isPaid ? activity.cost.rates.date : null;
          const paidRates = isPaid ? activity.cost.rates.data : null;
          
          if (isPaid) {
            clog(`Found paid activity during extraction: ${activity.name}`, {
              paidDate,
              hasRates: !!paidRates,
              ratesKeys: paidRates ? Object.keys(paidRates) : []
            });
          }
          
          // Add to the list
          costItems.push({
            id: costId,
            amount: costValue,
            currency: currency,
            activity: activity.name,
            type: activity.type,
            dayIndex: dayIndex, // Store day index for translation
            formattedAmount: `${costValue.toLocaleString()} ${currency}`,
            source: 'activity',
            dayId: day.id,
            isPaid,
            paidDate,
            paidRates
          });
          
          // Update totals
          totalByCurrency[currency] = (totalByCurrency[currency] || 0) + costValue;
        }
      }
    });
  });
  
  // Process additional costs if they exist
  if (parsedData.additionalCosts && Array.isArray(parsedData.additionalCosts)) {
    parsedData.additionalCosts.forEach((additionalCost, index) => {
      if (additionalCost.cost) {
        // Cost is always an object - simplify extraction
        const costValue = normalizeCurrencyValue(additionalCost.cost.value);
        const currency = additionalCost.cost.currency;
        
        // Only include if we have a cost value
        if (costValue && !isNaN(costValue) && costValue > 0) {
          // Find day index if day is specified
          let dayId = '';
          let dayIndex = null;
          
          if (additionalCost.day) {
            const foundDayIndex = parsedData.days.findIndex(d => d.id === additionalCost.day);
            if (foundDayIndex >= 0) {
              dayId = additionalCost.day;
              dayIndex = foundDayIndex;
            }
          }
          
          // Check if the cost has payment information
          const isPaid = !!additionalCost.cost.rates;
          const paidDate = isPaid ? additionalCost.cost.rates.date : null;
          const paidRates = isPaid ? additionalCost.cost.rates.data : null;
          const isSelected = additionalCost.isSelected !== undefined ? additionalCost.isSelected : true;
          
          if (isPaid) {
            clog(`Found paid additional cost during extraction: ${additionalCost.name}`, {
              paidDate,
              hasRates: !!paidRates,
              ratesKeys: paidRates ? Object.keys(paidRates) : []
            });
          }
          
          costItems.push({
            id: additionalCost.id,
            amount: costValue,
            currency: currency,
            activity: additionalCost.name,
            description: additionalCost.description || '',
            type: 'additional',
            dayIndex: dayIndex, // Store day index for translation (null if General)
            formattedAmount: `${costValue.toLocaleString()} ${currency}`,
            source: 'additional',
            dayId: dayId,
            isSelected: isSelected,
            isPaid,
            paidDate,
            paidRates
          });
          
          // Add to totals only if selected
          if (isSelected) {
            totalByCurrency[currency] = (totalByCurrency[currency] || 0) + costValue;
          }
        }
      }
    });
  }
  
  // Calculate totals
  const totals = Object.entries(totalByCurrency).map(([currency, amount]) => ({
    currency,
    totalAmount: amount,
    formattedTotal: `${amount.toLocaleString()} ${currency}`
  }));
  
  // Sort costs by amount (highest first)
  const sortedCosts = costItems.sort((a, b) => b.amount - a.amount);
  
  clog(`Extracted ${sortedCosts.length} cost items, ${totals.length} currencies`);
  
  return { items: sortedCosts, totals };
};

export default function CostsTab({ parsedData, isActive, onUpdatePlan }) {
  const { t } = useTranslation();
  const [selectedCurrency, setSelectedCurrencyState] = useState('USD');
  const [currencies, setCurrencies] = useState([]);
  const [convertedCosts, setConvertedCosts] = useState({ items: [], totals: [] });
  const [isConverting, setIsConverting] = useState(false);
  const [selectedItems, setSelectedItems] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showAddCostModal, setShowAddCostModal] = useState(false);
  const [showEditCostModal, setShowEditCostModal] = useState(false);
  const [currentEditCost, setCurrentEditCost] = useState(null);
  const [additionalCostForm, setAdditionalCostForm] = useState({
    name: '',
    description: '',
    cost: {
      value: '',
      currency: DEFAULT_CURRENCY
    },
    day: ''
  });
  
  // New state for Mark as Paid functionality
  const [showMarkAsPaidModal, setShowMarkAsPaidModal] = useState(false);
  const [currentPaidCost, setCurrentPaidCost] = useState(null);
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [isLoadingRates, setIsLoadingRates] = useState(false);
  
  // Extract costs using useMemo - runs when parsedData changes
  const costs = useMemo(() => {
    if (!parsedData) return { items: [], totals: [] };
    clog('Extracting costs from plan data...');
    return extractCosts(parsedData);
  }, [parsedData]);
  
  // Initialize selected items when costs change
  const initialSelectedItems = useMemo(() => {
    const state = {};
    costs.items.forEach(item => {
      state[item.id] = item.source === 'additional' 
        ? (item.isSelected !== undefined ? item.isSelected : true) 
        : true;
    });
    return state;
  }, [costs]);
  
  // Load currencies on mount
  useEffect(() => {
    const initCurrencies = async () => {
      const allCurrencies = await getAllCurrencies();
      setCurrencies(allCurrencies || []);
      setSelectedCurrencyState(getSelectedCurrency());
    };
    initCurrencies();
  }, []);
  
  // Sync selectedItems when costs change
  useEffect(() => {
    setSelectedItems(initialSelectedItems);
  }, [initialSelectedItems]);
  
  // Convert costs when currency changes or tab becomes active
  useEffect(() => {
    if (isActive && costs.items.length > 0) {
      clog('Converting costs to currency:', selectedCurrency);
      convertCostsToSelectedCurrency(costs, selectedCurrency);
    }
  }, [costs, selectedCurrency, isActive]);
  
  // Convert costs to selected currency
  const convertCostsToSelectedCurrency = async (costsData, targetCurrency) => {
    if (!costsData || !costsData.items || costsData.items.length === 0) {
      setConvertedCosts({ items: [], totals: [] });
      return;
    }
    
    setIsConverting(true);
    clog(`Converting ${costsData.items.length} costs to ${targetCurrency}...`);
    
    try {
      // Clone costs to avoid mutating the original
      const convertedItems = [];
      
      // Import the utility function once outside the loop
      const { convertCurrencyWithHistoricalRates } = require('../shared/currencyService');
      
      // Convert each cost item
      for (const cost of costsData.items) {
        let convertedAmount;
        
        // If the cost is marked as paid and has historical rates, use those
        if (cost.isPaid && cost.paidRates) {
          clog(`Converting paid cost "${cost.activity}" using historical rates from ${cost.paidDate}`);
          
          // Log the available rates
          if (cost.paidRates) {
            clog('Available historical rates:', Object.keys(cost.paidRates).join(', '));
          }
          
          // Convert using historical rates from the payment date
          try {
            convertedAmount = convertCurrencyWithHistoricalRates(
              cost.amount, 
              cost.currency, 
              targetCurrency,
              cost.paidRates
            );
            clog(`Converted ${cost.amount} ${cost.currency} to ${convertedAmount} ${targetCurrency} using historical rates`);
          } catch (error) {
            cerror('Error converting with historical rates:', error);
            // Fallback to current rates
            convertedAmount = await convertCurrency(cost.amount, cost.currency, targetCurrency);
            clog(`Fallback: Converted ${cost.amount} ${cost.currency} to ${convertedAmount} ${targetCurrency} using current rates`);
          }
        } else {
          // Otherwise use current rates
          convertedAmount = await convertCurrency(cost.amount, cost.currency, targetCurrency);
          // Remove console.log to prevent spam when saving activities
        }
        
        convertedItems.push({
          ...cost,
          originalAmount: cost.amount,
          originalCurrency: cost.currency,
          amount: convertedAmount,
          currency: targetCurrency,
          formattedAmount: `${convertedAmount.toLocaleString()} ${targetCurrency}`
        });
      }
      
      // Sort costs by amount (highest first) for initial display
      const sortedCosts = convertedItems.sort((a, b) => b.amount - a.amount);
      
      // Remove console.log to prevent spam when saving activities
      setConvertedCosts({ items: sortedCosts, totals: [] });
    } catch (error) {
      cerror('Error converting costs:', error);
      alert(`Error converting costs: ${error.message}`);
    } finally {
      setIsConverting(false);
    }
  };
  
  // Handle currency selection change
  const handleCurrencyChange = (e) => {
    const currency = e.target.value;
    setSelectedCurrencyState(currency);
    setSelectedCurrency(currency); // Save to storage
  };
  
  // Determine which costs to display
  // Always use converted costs for consistent behavior
  const displayCosts = convertedCosts;
  
  // Get the currency symbol
  const getCurrencySymbol = (code) => {
    const currency = currencies.find(c => c.code === code);
    return currency ? currency.symbol : '';
  };
  
  // Handle checkbox change for an item
  const handleItemSelection = (costId) => {
    // Update the UI state
    const newSelectedState = {
      ...selectedItems,
      [costId]: !selectedItems[costId]
    };
    setSelectedItems(newSelectedState);
    
    // For additional costs, update the isSelected property in the data
    const costItem = costs.items.find(item => item.id === costId);
    if (costItem && costItem.source === 'additional') {
      // Create a copy of parsed data to modify
      const updatedParsedData = { ...parsedData };
      
      // Find and update the isSelected property
      if (updatedParsedData.additionalCosts) {
        const updated = updatedParsedData.additionalCosts.map(cost => {
          if (cost.id === costId) {
            return { ...cost, isSelected: !selectedItems[costId] };
          }
          return cost;
        });
        
        updatedParsedData.additionalCosts = updated;
        
        // Save the changes to the server
        updatePlanData(updatedParsedData);
      }
    }
  };
  
  // Calculate totals based on selected items - memoize to prevent recalculation
  const selectedTotals = useMemo(() => {
    if (!displayCosts || !displayCosts.items) return [];
    
    let totalByCurrency = {};
    
    // Add up only selected items
    displayCosts.items.forEach(item => {
      if (selectedItems[item.id]) {
        const currency = item.currency;
        totalByCurrency[currency] = (totalByCurrency[currency] || 0) + item.amount;
      }
    });
    
    // Format totals
    return Object.entries(totalByCurrency).map(([currency, amount]) => ({
      currency,
      totalAmount: amount,
      formattedTotal: `${amount.toLocaleString()} ${currency}`
    }));
  }, [displayCosts, selectedItems]);
  
  // Get sorted items - memoize to prevent recalculation
  const sortedItems = useMemo(() => {
    if (!displayCosts || !displayCosts.items) return [];
    
    // Create a copy to avoid mutation
    const sortableItems = [...displayCosts.items];
    
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        let aValue, bValue;
        
        // Extract the values to compare based on the sort key
        switch (sortConfig.key) {
          case 'day':
            // Sort by day index (null/undefined = General, comes last)
            const aDayNum = a.dayIndex !== null && a.dayIndex !== undefined ? a.dayIndex + 1 : 9999;
            const bDayNum = b.dayIndex !== null && b.dayIndex !== undefined ? b.dayIndex + 1 : 9999;
            
            if (aDayNum !== bDayNum) {
              return sortConfig.direction === 'asc' 
                ? aDayNum - bDayNum 
                : bDayNum - aDayNum;
            }
            // If days are equal, don't apply secondary sorting
            return 0;
            
          case 'type':
            aValue = a.type.toLowerCase();
            bValue = b.type.toLowerCase();
            break;
          case 'amount':
            aValue = a.amount;
            bValue = b.amount;
            break;
          default:
            aValue = a[sortConfig.key];
            bValue = b[sortConfig.key];
        }
        
        // Perform the comparison
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return sortableItems;
  }, [displayCosts, sortConfig]);
  
  // Request a sort by column
  const requestSort = (key) => {
    let direction = 'asc';
    
    // If already sorting by this key, toggle direction
    if (sortConfig.key === key) {
      direction = sortConfig.direction === 'asc' ? 'desc' : 'asc';
    }
    
    setSortConfig({ key, direction });
  };

  // Handle editing an additional cost
  const handleEditAdditionalCost = (costItem) => {
    if (costItem.source !== 'additional') return;
    
    setCurrentEditCost(costItem);
    setAdditionalCostForm({
      name: costItem.activity,
      description: costItem.description || '',
      cost: {
        // USE ORIGINAL CURRENCY AND AMOUNT, NOT CONVERTED ONES!
        value: (costItem.originalAmount || costItem.amount).toString(),
        currency: costItem.originalCurrency || costItem.currency
      },
      day: costItem.dayId || ''
    });
    setShowEditCostModal(true);
  };
  
  // Handle deleting an additional cost
  const handleDeleteAdditionalCost = (costId) => {
    // Find the cost item
    const costItem = costs.items.find(item => item.id === costId);
    if (!costItem || costItem.source !== 'additional') return;
    
    // Create a copy of parsed data to modify
    const updatedParsedData = { ...parsedData };
    
    // Ensure additionalCosts exists
    if (!updatedParsedData.additionalCosts) {
      updatedParsedData.additionalCosts = [];
      return;
    }
    
    clog('Deleting cost with ID:', costId);
    clog('Current additionalCosts:', updatedParsedData.additionalCosts);
    
    // Remove the cost from additionalCosts array
    const previousLength = updatedParsedData.additionalCosts.length;
    updatedParsedData.additionalCosts = updatedParsedData.additionalCosts.filter(
      cost => cost.id !== costId
    );
    
    clog('Updated additionalCosts:', updatedParsedData.additionalCosts);
    clog('Items removed:', previousLength - updatedParsedData.additionalCosts.length);
    
    // Only update if something changed
    if (previousLength !== updatedParsedData.additionalCosts.length) {
      // Update the plan data through API
      updatePlanData(updatedParsedData);
    } else {
      cerror('Could not find cost with ID:', costId);
    }
  };
  
  // Save a new additional cost
  const handleSaveAdditionalCost = () => {
    // Validate form
    if (!additionalCostForm.name || !additionalCostForm.cost.value) {
      // Show validation error
      return;
    }
    
    // Create a copy of parsed data to modify
    const updatedParsedData = { ...parsedData };
    
    // Initialize additionalCosts array if it doesn't exist
    if (!updatedParsedData.additionalCosts) {
      updatedParsedData.additionalCosts = [];
    }
    
    // Create the new cost object
    const newCost = {
      id: generateUUID(), // Use UUID for consistent ID format
      name: additionalCostForm.name,
      description: additionalCostForm.description,
      cost: {
        value: additionalCostForm.cost.value,
        currency: additionalCostForm.cost.currency
      },
      day: additionalCostForm.day || '',
      isSelected: true // Add isSelected property set to true by default
    };
    
    // Add to the additionalCosts array
    updatedParsedData.additionalCosts.push(newCost);
    
    // Update the plan data through API
    updatePlanData(updatedParsedData);
    
    // Close the modal and reset form
    setShowAddCostModal(false);
    setAdditionalCostForm({
      name: '',
      description: '',
      cost: {
        value: '',
        currency: DEFAULT_CURRENCY
      },
      day: ''
    });
  };
  
  // Update an existing additional cost
  const handleUpdateAdditionalCost = () => {
    if (!currentEditCost) return;
    
    // Create a copy of parsed data to modify
    const updatedParsedData = { ...parsedData };
    
    // Find and update the cost
    if (updatedParsedData.additionalCosts) {
      const index = updatedParsedData.additionalCosts.findIndex(
        cost => cost.id === currentEditCost.id
      );
      
      if (index >= 0) {
        updatedParsedData.additionalCosts[index] = {
          id: currentEditCost.id,
          name: additionalCostForm.name,
          description: additionalCostForm.description,
          cost: {
            value: additionalCostForm.cost.value,
            currency: additionalCostForm.cost.currency
          },
          day: additionalCostForm.day || ''
        };
        
        // Update the plan data through API
        updatePlanData(updatedParsedData);
      }
    }
    
    // Close the modal and reset
    setShowEditCostModal(false);
    setCurrentEditCost(null);
  };
  
  // Function to update plan data via centralized saveChanges
  const updatePlanData = async (updatedData) => {
    if (!onUpdatePlan) {
      cerror('onUpdatePlan function not provided');
      return;
    }
    
    try {
      // Use the centralized saveChanges function from PlanDetail
      // This updates parsedData in parent, which triggers useMemo to recompute costs
      await onUpdatePlan(updatedData);
    } catch (error) {
      cerror('Error updating plan:', error);
    }
  };
  
  // Handle form input changes for additional cost form
  const handleCostFormChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'costValue') {
      setAdditionalCostForm(prev => ({
        ...prev,
        cost: {
          ...prev.cost,
          value: value
        }
      }));
    } else if (name === 'costCurrency') {
      setAdditionalCostForm(prev => ({
        ...prev,
        cost: {
          ...prev.cost,
          currency: value
        }
      }));
    } else {
      setAdditionalCostForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  // Get available days for dropdown
  const availableDays = parsedData?.days?.map((day, index) => ({
    id: day.id,
    label: `${t('planDetail.costs.day')} ${index + 1} (${formatDateWithLocale(day.date, 'PPP')})`
  })) || [];

  // Handle opening the Mark as Paid modal
  const handleOpenMarkAsPaidModal = (cost) => {
    setCurrentPaidCost(cost);
    setPaymentDate(new Date().toISOString().split('T')[0]); // Default to today
    setShowMarkAsPaidModal(true);
  };
  
  // Handle payment date change
  const handlePaymentDateChange = (e) => {
    setPaymentDate(e.target.value);
  };
  
  // Handle marking a cost as paid
  const handleMarkAsPaid = async () => {
    if (!currentPaidCost || !paymentDate) return;
    
    setIsLoadingRates(true);
    
    try {
      // Ensure date is in YYYY-MM-DD format
      const formattedDate = new Date(paymentDate).toISOString().split('T')[0];
      clog('Marking cost as paid with date:', formattedDate);
      
      // Fetch historical rates for the payment date
      const historicalRates = await getHistoricalRates(formattedDate);
      clog('Received historical rates:', historicalRates);
      
      // Create a copy of parsed data to modify
      const updatedParsedData = { ...parsedData };
      
      // Find the cost item to update
      if (currentPaidCost.source === 'additional' && updatedParsedData.additionalCosts) {
        // Update additional cost
        const updated = updatedParsedData.additionalCosts.map(cost => {
          if (cost.id === currentPaidCost.id) {
            clog('Updating additional cost:', cost.name);
            return { 
              ...cost, 
              cost: {
                ...cost.cost,
                rates: {
                  date: formattedDate,
                  data: historicalRates
                }
              }
            };
          }
          return cost;
        });
        
        updatedParsedData.additionalCosts = updated;
        clog('Updated additionalCosts array:', updatedParsedData.additionalCosts);
      } else if (currentPaidCost.source === 'activity' && updatedParsedData.days) {
        // Extract day and activity index from the cost ID (format: day{dayIndex}_act{actIndex})
        const match = currentPaidCost.id.match(/day(\d+)_act(\d+)/);
        if (match) {
          const dayIndex = parseInt(match[1]);
          const actIndex = parseInt(match[2]);
          
          clog(`Updating activity at day ${dayIndex}, activity ${actIndex}`);
          
          // Update the activity if indices are valid
          if (updatedParsedData.days[dayIndex] && 
              updatedParsedData.days[dayIndex].activities && 
              updatedParsedData.days[dayIndex].activities[actIndex]) {
            
            const activity = updatedParsedData.days[dayIndex].activities[actIndex];
            clog('Original activity:', activity);
            
            updatedParsedData.days[dayIndex].activities[actIndex] = {
              ...activity,
              cost: {
                ...activity.cost,
                rates: {
                  date: formattedDate,
                  data: historicalRates
                }
              }
            };
            
            clog('Updated activity:', updatedParsedData.days[dayIndex].activities[actIndex]);
          }
        }
      }
      
      // Save the changes to the server
      clog('Saving updated plan data...');
      await updatePlanData(updatedParsedData);
      
      // Close the modal
      setShowMarkAsPaidModal(false);
      // parsedData update triggers useMemo to recompute costs
      
    } catch (error) {
      cerror('Error marking cost as paid:', error);
      alert('Failed to mark cost as paid: ' + error.message);
    } finally {
      setIsLoadingRates(false);
    }
  };
  
  // Handle resetting a paid cost
  const handleResetPayment = async (cost) => {
    try {
      clog('Resetting payment for:', cost.activity);
      
      // Create a copy of parsed data to modify
      const updatedParsedData = { ...parsedData };
      
      // Find the cost item to update
      if (cost.source === 'additional' && updatedParsedData.additionalCosts) {
        // Update additional cost
        const updated = updatedParsedData.additionalCosts.map(item => {
          if (item.id === cost.id) {
            clog('Resetting additional cost payment:', item.name);
            // Create a new cost object without the rates property
            const { rates, ...restCost } = item.cost;
            return { 
              ...item, 
              cost: restCost
            };
          }
          return item;
        });
        
        updatedParsedData.additionalCosts = updated;
      } else if (cost.source === 'activity' && updatedParsedData.days) {
        // Extract day and activity index from the cost ID (format: day{dayIndex}_act{actIndex})
        const match = cost.id.match(/day(\d+)_act(\d+)/);
        if (match) {
          const dayIndex = parseInt(match[1]);
          const actIndex = parseInt(match[2]);
          
          clog(`Resetting activity payment at day ${dayIndex}, activity ${actIndex}`);
          
          // Update the activity if indices are valid
          if (updatedParsedData.days[dayIndex] && 
              updatedParsedData.days[dayIndex].activities && 
              updatedParsedData.days[dayIndex].activities[actIndex]) {
            
            const activity = updatedParsedData.days[dayIndex].activities[actIndex];
            
            // Create a new cost object without the rates property
            const { rates, ...restCost } = activity.cost;
            
            updatedParsedData.days[dayIndex].activities[actIndex] = {
              ...activity,
              cost: restCost
            };
          }
        }
      }
      
      // Save the changes to the server
      clog('Saving updated plan data after payment reset...');
      await updatePlanData(updatedParsedData);
      // parsedData update triggers useMemo to recompute costs
    } catch (error) {
      cerror('Error resetting payment:', error);
    }
  };
  
  return (
    <Card>
      <Card.Body>
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
          <h4 className="mb-0">{t('planDetail.costs.costAnalysis')}</h4>
          
          <div className="d-flex flex-wrap align-items-center gap-2">
            <Button 
              variant="success" 
              onClick={() => setShowAddCostModal(true)}
              size="sm"
            >
              <i className="bi bi-plus-circle me-1"></i> {t('planDetail.costs.addCost')}
            </Button>
            
            <div className="d-flex align-items-center gap-2">
              <Form.Label className="mb-0">{t('planDetail.costs.convertTo')}</Form.Label>
              <Form.Select 
                value={selectedCurrency} 
                onChange={handleCurrencyChange}
                disabled={isConverting}
                size="sm"
                style={{ width: 'auto', minWidth: '120px' }}
              >
                {currencies.map(currency => (
                  <option key={currency.code} value={currency.code}>
                    {currency.code} ({currency.symbol})
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>
        </div>
        
        {isConverting && (
          <Alert variant="info">
            <Spinner animation="border" size="sm" className="me-2" /> 
            {t('planDetail.costs.converting', { currency: selectedCurrency })}
          </Alert>
        )}
        
        {displayCosts.items.length === 0 ? (
          <Alert variant="info">
            {t('planDetail.costs.noCostsFound')}
          </Alert>
        ) : (
          <>
            <Row className="mb-4">
              <Col md={6}>
                <Card className="bg-light">
                  <Card.Body>
                    <h5 className="mb-3">{t('planDetail.costs.costSummary')}</h5>
                    <ListGroup variant="flush">
                      {selectedTotals.map((total, index) => (
                        <ListGroup.Item key={index} className="bg-light d-flex justify-content-between">
                          <strong>{t('planDetail.costs.total')} ({getCurrencySymbol(total.currency)} {total.currency}):</strong>
                          <span className="fw-bold">{total.formattedTotal}</span>
                        </ListGroup.Item>
                      ))}
                      
                      {/* Payment Status Totals */}
                      {(() => {
                        const paidTotal = displayCosts.items
                          .filter(item => item.isPaid && selectedItems[item.id])
                          .reduce((sum, item) => sum + item.amount, 0);
                        const unpaidTotal = displayCosts.items
                          .filter(item => !item.isPaid && selectedItems[item.id])
                          .reduce((sum, item) => sum + item.amount, 0);
                        
                        return (
                          <>
                            <ListGroup.Item className="bg-light d-flex justify-content-between">
                              <span className="text-success">
                                <i className="bi bi-check-circle me-1"></i>
                                <strong>{t('planDetail.costs.totalPaid')}</strong>
                              </span>
                              <span className="fw-bold text-success">
                                {getCurrencySymbol(selectedCurrency)} {paidTotal.toLocaleString()} {selectedCurrency}
                              </span>
                            </ListGroup.Item>
                            <ListGroup.Item className="bg-light d-flex justify-content-between">
                              <span className="text-warning">
                                <i className="bi bi-clock me-1"></i>
                                <strong>{t('planDetail.costs.notPaidYet')}</strong>
                              </span>
                              <span className="fw-bold text-warning">
                                {getCurrencySymbol(selectedCurrency)} {unpaidTotal.toLocaleString()} {selectedCurrency}
                              </span>
                            </ListGroup.Item>
                          </>
                        );
                      })()}
                      
                    </ListGroup>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            
            <h5 className="mb-3">{t('planDetail.costs.itemizedCosts')}</h5>
            
            {/* Table view for medium screens and larger */}
            <div className="d-none d-md-block">
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{t('planDetail.costs.include')}</th>
                    <th>{t('planDetail.costs.name')}</th>
                    <th 
                      onClick={() => requestSort('day')}
                      style={{ cursor: 'pointer' }}
                      className="user-select-none"
                    >
                      {t('planDetail.costs.day')} {sortConfig.key === 'day' && (
                        <i className={`bi bi-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}`}></i>
                      )}
                    </th>
                    <th 
                      onClick={() => requestSort('type')}
                      style={{ cursor: 'pointer' }}
                      className="user-select-none"
                    >
                      {t('planDetail.costs.type')} {sortConfig.key === 'type' && (
                        <i className={`bi bi-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}`}></i>
                      )}
                    </th>
                    <th>{t('planDetail.costs.originalAmount')}</th>
                    <th 
                      onClick={() => requestSort('amount')}
                      style={{ cursor: 'pointer' }}
                      className="user-select-none"
                    >
                      {t('planDetail.costs.amount')} {sortConfig.key === 'amount' && (
                        <i className={`bi bi-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}`}></i>
                      )}
                    </th>
                    <th>{t('planDetail.costs.conversionRate')}</th>
                    <th>{t('planDetail.costs.status')}</th>
                    <th>{t('planDetail.costs.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedItems.map((cost, index) => {
                    // Calculate the conversion rate
                    const conversionRate = cost.amount / cost.originalAmount;
                    const isAdditionalCost = cost.source === 'additional';
                    const isPaid = cost.isPaid;
                    
                    return (
                      <tr key={cost.id}>
                        <td style={{ width: '50px', textAlign: 'center', padding: '8px' }}>{index + 1}</td>
                        <td style={{ width: '70px', textAlign: 'center', padding: '8px' }}>
                          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '20px' }}>
                            <Form.Check 
                              type="checkbox"
                              checked={selectedItems[cost.id] || false}
                              onChange={() => handleItemSelection(cost.id)}
                              aria-label={`Select cost item ${index + 1}`}
                            />
                          </div>
                        </td>
                        <td>
                          {cost.activity}
                          {isAdditionalCost && cost.description && (
                            <div className="small text-muted">{cost.description}</div>
                          )}
                        </td>
                        <td>{cost.dayIndex !== null && cost.dayIndex !== undefined ? `${t('planDetail.costs.day')} ${cost.dayIndex + 1}` : t('planDetail.costs.general')}</td>
                        <td>
                          <Badge bg={isAdditionalCost ? 'warning' : getActivityStyle(cost.type).bg}>
                            {(() => {
                              if (isAdditionalCost) return t('planDetail.costs.additional');
                              const typeLower = (cost.type || '').toLowerCase();
                              if (typeLower === 'attraction') return t('planDetail.days.attraction');
                              if (typeLower === 'accommodation') return t('planDetail.days.accommodation');
                              if (typeLower === 'transportation') return t('planDetail.days.transportation');
                              return cost.type;
                            })()}
                          </Badge>
                        </td>
                        <td>
                          {getCurrencySymbol(cost.originalCurrency)} {cost.originalAmount.toLocaleString()} {cost.originalCurrency}
                        </td>
                        <td className="fw-bold">
                          {getCurrencySymbol(cost.currency)} {cost.formattedAmount}
                        </td>
                        <td>
                          {conversionRate.toFixed(4)}
                        </td>
                        <td>
                          {isPaid ? (
                            <Badge bg="success">
                              {t('planDetail.costs.paidOn', { 
                                date: formatDateWithLocale(cost.paidDate, 'PPP')
                              })}
                            </Badge>
                          ) : (
                            <Badge bg="secondary">{t('planDetail.costs.unpaid')}</Badge>
                          )}
                        </td>
                        <td>
                          <div className="d-flex">
                            {!isPaid && (
                              <Button 
                                variant="outline-success" 
                                size="sm"
                                className="me-1" 
                                onClick={() => handleOpenMarkAsPaidModal(cost)}
                              >
                                <i className="bi bi-check-circle"></i>
                              </Button>
                            )}
                            
                            {isPaid && (
                              <Button 
                                variant="outline-danger" 
                                size="sm"
                                className="me-1" 
                                onClick={() => handleResetPayment(cost)}
                              >
                                <i className="bi bi-x-circle"></i>
                              </Button>
                            )}
                            
                            {isAdditionalCost && (
                              <>
                                <Button 
                                  variant="outline-primary" 
                                  size="sm"
                                  className="me-1" 
                                  onClick={() => handleEditAdditionalCost(cost)}
                                >
                                  <i className="bi bi-pencil"></i>
                                </Button>
                                <Button 
                                  variant="outline-danger" 
                                  size="sm" 
                                  onClick={() => handleDeleteAdditionalCost(cost.id)}
                                >
                                  <i className="bi bi-trash"></i>
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
            
            {/* Card view for small screens */}
            <div className="d-md-none">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    onClick={() => requestSort('day')}
                    className="me-2 mb-2"
                  >
                    {t('planDetail.costs.sortByDay')} {sortConfig.key === 'day' && (
                      <i className={`bi bi-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </Button>
                  <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    onClick={() => requestSort('type')}
                    className="me-2 mb-2"
                  >
                    {t('planDetail.costs.sortByType')} {sortConfig.key === 'type' && (
                      <i className={`bi bi-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </Button>
                  <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    onClick={() => requestSort('amount')}
                    className="mb-2"
                  >
                    {t('planDetail.costs.sortByAmount')} {sortConfig.key === 'amount' && (
                      <i className={`bi bi-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </Button>
                </div>
              </div>
              
              {sortedItems.map((cost, index) => {
                const isAdditionalCost = cost.source === 'additional';
                const style = isAdditionalCost ? { bg: 'warning', icon: 'bi-tag' } : getActivityStyle(cost.type);
                const isPaid = cost.isPaid;
                
                return (
                  <Card key={cost.id} className="mb-3 shadow-sm">
                    <Card.Header className={`bg-${style.bg} bg-opacity-25 d-flex justify-content-between align-items-center`}>
                      <div className="d-flex align-items-center">
                        <Form.Check 
                          type="checkbox"
                          checked={selectedItems[cost.id] || false}
                          onChange={() => handleItemSelection(cost.id)}
                          aria-label={`Select cost item ${index + 1}`}
                          className="me-2"
                        />
                        <span className="fw-bold">{index + 1}. {cost.activity}</span>
                      </div>
                      <div className="d-flex align-items-center">
                        {isPaid ? (
                          <Badge bg="success" className="me-2">{t('planDetail.costs.paid')}</Badge>
                        ) : (
                          <Badge bg="secondary" className="me-2">{t('planDetail.costs.unpaid')}</Badge>
                        )}
                        {isAdditionalCost && (
                          <>
                            <Button 
                              variant="outline-primary" 
                              size="sm"
                              className="me-1" 
                              onClick={() => handleEditAdditionalCost(cost)}
                            >
                              <i className="bi bi-pencil"></i>
                            </Button>
                            <Button 
                              variant="outline-danger" 
                              size="sm" 
                              onClick={() => handleDeleteAdditionalCost(cost.id)}
                            >
                              <i className="bi bi-trash"></i>
                            </Button>
                          </>
                        )}
                      </div>
                    </Card.Header>
                    <Card.Body className="py-2">
                      <Row className="g-2">
                        <Col xs={6}>
                          <div className="small text-muted">{t('planDetail.costs.day')}</div>
                          <div>{cost.dayIndex !== null && cost.dayIndex !== undefined ? `${t('planDetail.costs.day')} ${cost.dayIndex + 1}` : t('planDetail.costs.general')}</div>
                        </Col>
                        <Col xs={6}>
                          <div className="small text-muted">{t('planDetail.costs.type')}</div>
                          <div>
                            <Badge bg={style.bg}>
                              <i className={`bi ${style.icon} me-1`}></i>
                              {(() => {
                                if (isAdditionalCost) return t('planDetail.costs.additional');
                                const typeLower = (cost.type || '').toLowerCase();
                                if (typeLower === 'attraction') return t('planDetail.days.attraction');
                                if (typeLower === 'accommodation') return t('planDetail.days.accommodation');
                                if (typeLower === 'transportation') return t('planDetail.days.transportation');
                                return cost.type;
                              })()}
                            </Badge>
                          </div>
                        </Col>
                        <Col xs={12}>
                          <hr className="my-2" />
                        </Col>
                        <Col xs={6}>
                          <div className="small text-muted">{t('planDetail.costs.originalAmount')}</div>
                          <div>{getCurrencySymbol(cost.originalCurrency)} {cost.originalAmount.toLocaleString()} {cost.originalCurrency}</div>
                        </Col>
                        <Col xs={6}>
                          <div className="small text-muted">{t('planDetail.costs.convertedAmount')}</div>
                          <div className="fw-bold">{getCurrencySymbol(cost.currency)} {cost.formattedAmount}</div>
                        </Col>
                        {isAdditionalCost && cost.description && (
                          <Col xs={12}>
                            <div className="small text-muted mt-2">{t('planDetail.costs.description')}</div>
                            <div className="small">{cost.description}</div>
                          </Col>
                        )}
                        
                        {isPaid && (
                          <Col xs={12} className="mt-2">
                            <div className="small text-muted">{t('planDetail.costs.paymentDate')}</div>
                            <div className="small text-success">
                              <i className="bi bi-calendar-check me-1"></i>
                              {t('planDetail.costs.paidOn', { 
                                date: formatDateWithLocale(cost.paidDate, 'PPP')
                              })}
                            </div>
                          </Col>
                        )}
                        
                        <Col xs={12} className="mt-2">
                          {isPaid ? (
                            <Button 
                              variant="outline-danger" 
                              size="sm"
                              className="w-100" 
                              onClick={() => handleResetPayment(cost)}
                            >
                              <i className="bi bi-x-circle me-1"></i>
                              {t('planDetail.costs.resetPayment')}
                            </Button>
                          ) : (
                            <Button 
                              variant="outline-success" 
                              size="sm"
                              className="w-100" 
                              onClick={() => handleOpenMarkAsPaidModal(cost)}
                            >
                              <i className="bi bi-check-circle me-1"></i>
                              {t('planDetail.costs.markAsPaid')}
                            </Button>
                          )}
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                );
              })}
            </div>
          </>
        )}
        
        {/* Add Cost Modal */}
        <Modal show={showAddCostModal} onHide={() => setShowAddCostModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>{t('planDetail.costs.addAdditionalCost')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>{t('planDetail.costs.name')}</Form.Label>
                <Form.Control 
                  type="text" 
                  name="name" 
                  value={additionalCostForm.name}
                  onChange={handleCostFormChange}
                  placeholder={t('planDetail.costs.namePlaceholder')}
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>{t('planDetail.costs.description')}</Form.Label>
                <Form.Control 
                  as="textarea" 
                  rows={2}
                  name="description" 
                  value={additionalCostForm.description}
                  onChange={handleCostFormChange}
                  placeholder={t('planDetail.costs.descriptionPlaceholder')}
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>{t('planDetail.costs.amount')}</Form.Label>
                <Row>
                  <Col md={6}>
                    <Form.Control 
                      type="text" 
                      name="costValue" 
                      value={additionalCostForm.cost.value}
                      onChange={handleCostFormChange}
                      placeholder={t('planDetail.costs.costPlaceholder')}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Select 
                      name="costCurrency" 
                      value={additionalCostForm.cost.currency}
                      onChange={handleCostFormChange}
                    >
                      {currencies.map(currency => (
                        <option key={currency.code} value={currency.code}>
                          {currency.code} ({currency.symbol})
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                </Row>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>{t('planDetail.costs.associatedDay')}</Form.Label>
                <Form.Select
                  name="day"
                  value={additionalCostForm.day}
                  onChange={handleCostFormChange}
                >
                  <option value="">{t('planDetail.costs.general')}</option>
                  {availableDays.map(day => (
                    <option key={day.id} value={day.id}>{day.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddCostModal(false)}>
              {t('planDetail.costs.cancel')}
            </Button>
            <Button variant="primary" onClick={handleSaveAdditionalCost}>
              {t('planDetail.costs.save')}
            </Button>
          </Modal.Footer>
        </Modal>
        
        {/* Edit Cost Modal */}
        <Modal show={showEditCostModal} onHide={() => setShowEditCostModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>{t('planDetail.costs.editAdditionalCost')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>{t('planDetail.costs.name')}</Form.Label>
                <Form.Control 
                  type="text" 
                  name="name" 
                  value={additionalCostForm.name}
                  onChange={handleCostFormChange}
                  placeholder={t('planDetail.costs.namePlaceholder')}
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>{t('planDetail.costs.description')}</Form.Label>
                <Form.Control 
                  as="textarea" 
                  rows={2}
                  name="description" 
                  value={additionalCostForm.description}
                  onChange={handleCostFormChange}
                  placeholder={t('planDetail.costs.descriptionPlaceholder')}
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>{t('planDetail.costs.amount')}</Form.Label>
                <Row>
                  <Col md={6}>
                    <Form.Control 
                      type="text" 
                      name="costValue" 
                      value={additionalCostForm.cost.value}
                      onChange={handleCostFormChange}
                      placeholder={t('planDetail.costs.costPlaceholder')}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Select 
                      name="costCurrency" 
                      value={additionalCostForm.cost.currency}
                      onChange={handleCostFormChange}
                    >
                      {currencies.map(currency => (
                        <option key={currency.code} value={currency.code}>
                          {currency.code} ({currency.symbol})
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                </Row>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>{t('planDetail.costs.associatedDay')}</Form.Label>
                <Form.Select
                  name="day"
                  value={additionalCostForm.day}
                  onChange={handleCostFormChange}
                >
                  <option value="">{t('planDetail.costs.general')}</option>
                  {availableDays.map(day => (
                    <option key={day.id} value={day.id}>{day.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditCostModal(false)}>
              {t('planDetail.costs.cancel')}
            </Button>
            <Button variant="primary" onClick={handleUpdateAdditionalCost}>
              {t('planDetail.costs.update')}
            </Button>
          </Modal.Footer>
        </Modal>
        
        {/* Mark as Paid Modal */}
        <Modal show={showMarkAsPaidModal} onHide={() => setShowMarkAsPaidModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>{t('planDetail.costs.markCostAsPaid')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {currentPaidCost && (
              <>
                <p dangerouslySetInnerHTML={{ __html: t('planDetail.costs.markAsPaidConfirm', { name: currentPaidCost.activity, amount: currentPaidCost.formattedAmount }) }} />
                
                <Form.Group className="mb-3">
                  <Form.Label>{t('planDetail.costs.paymentDate')}</Form.Label>
                  <Form.Control 
                    type="date" 
                    value={paymentDate}
                    onChange={handlePaymentDateChange}
                    max={new Date().toISOString().split('T')[0]} // Can't select future dates
                  />
                  <Form.Text className="text-muted">
                    {t('planDetail.costs.paymentDateHelp')}
                  </Form.Text>
                </Form.Group>
                
                {isLoadingRates && (
                  <Alert variant="info">
                    <Spinner animation="border" size="sm" className="me-2" /> 
                    {t('planDetail.costs.fetchingRates', { date: paymentDate })}
                  </Alert>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowMarkAsPaidModal(false)}>
              {t('planDetail.costs.cancel')}
            </Button>
            <Button 
              variant="success" 
              onClick={handleMarkAsPaid}
              disabled={isLoadingRates}
            >
              {t('planDetail.costs.markAsPaid')}
            </Button>
          </Modal.Footer>
        </Modal>
      </Card.Body>
    </Card>
  );
} 